import { useEffect, useMemo, useRef, type PropsWithChildren } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, type Resolver } from "react-hook-form";
import { injectIntl, type intlFormatPropTypes } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

import {
  buildCountryOptions,
  buildZoneOptions,
  findCountryByCode,
} from "../../../AddressForm";
import type { Country } from "../../../AddressForm/AddressForm.types";
import {
  selectProfileIsHost,
  selectProfileSSOid,
} from "../../../app-main/selectors/profile.selectors";
import { selectAccountCountry } from "../../../app-main/selectors/space.selectors";
import type { TaxDetailsScope } from "../../TaxDetails/TaxDetails.types";
import {
  findFeeRegion,
  getCountryTaxTypes,
  getRegionTaxTypes,
  resolveScopedTax,
} from "../../TaxDetails/TaxDetails.utils";
import type { TaxDetailsFormValues } from "../TaxDetailsFormSection.types";
import {
  buildTaxDetailsFormDefaults,
  getLocaleCountry,
  getRegionLabel,
  getRegionName,
  isCountryLocked as getIsCountryLocked,
  isRegionSelectionRequired,
  isScopedTaxAvailable,
  resolveTaxTypeName,
} from "../TaxDetailsFormSection.utils";
import { useRegionalTaxTypes } from "../useRegionalTaxTypes";
import { useTaxDetailsFormRegistration } from "../useTaxDetailsFormRegistration";

import { createTaxDetailsFormSchema } from "./TaxDetailsForm.schema";
import { submitTaxDetailsForm } from "./TaxDetailsForm.submit";
import { TaxDetailsFormContext } from "./TaxDetailsFormContext";
import type { TaxDetailsFormContextType } from "./TaxDetailsFormContext";

type Dispatch = (action: unknown) => Promise<unknown>;

type TaxDetailsFormProviderProps = PropsWithChildren<{
  accessToken: string;
  /** Countries in the reader's locale — drives every label they see. */
  allCountries: Country[];
  /** The same countries in English — drives validation, see `resolver` below. */
  globalCountries: Country[];
  intl: intlFormatPropTypes;
  scope: TaxDetailsScope;
}>;

/**
 * Owns the tax details form: which registration it is for, what it opens with,
 * how it validates and what submitting does. The views below it only render.
 */
const TaxDetailsFormProviderComponent = ({
  accessToken,
  allCountries,
  children,
  globalCountries,
  intl,
  scope,
}: TaxDetailsFormProviderProps) => {
  const dispatch = useDispatch() as Dispatch;
  const accountCountry = useSelector(selectAccountCountry);
  const isHost = useSelector(selectProfileIsHost);
  const ssoId = useSelector(selectProfileSSOid);

  const {
    isEdit,
    isSubregion,
    registration,
    registrationId,
    savedTaxTypes,
    vatProfile,
  } = useTaxDetailsFormRegistration(scope);

  // What the schema needs that depends on the selected region. Held in a ref
  // because the resolver is built before the region is known — it runs after the
  // render that resolves it, so reading the ref keeps the two in step without
  // rebuilding the form.
  const taxContext = useRef({
    hasScopedTax: true,
    taxTypeName: "",
  });

  const resolver: Resolver<TaxDetailsFormValues> = (values, context, options) =>
    zodResolver(
      createTaxDetailsFormSchema({
        intl,
        isHost,
        isSubregion,
        selectedCountry: findCountryByCode(globalCountries, values.country),
        ...taxContext.current,
      }),
    )(values, context, options);

  const form = useForm<TaxDetailsFormValues>({
    resolver,
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: buildTaxDetailsFormDefaults({
      accountCountry,
      isHost,
      localeCountry: getLocaleCountry(intl.locale),
      vatProfile,
    }),
  });

  // A zone belongs to its country, and the schema only checks that one is picked,
  // so a state kept from the previous country would submit as-is. Only a change
  // the user makes clears it, so an edit still opens with the state on file.
  useEffect(() => {
    const subscription = form.watch((_values, { name, type }) => {
      if ("country" === name && "change" === type) {
        form.setValue("state", "");
      }
    });

    return () => subscription.unsubscribe();
  }, [form]);

  const country = form.watch("country");
  const state = form.watch("state");

  const {
    feeTypes,
    isLoading: areFeesLoading,
    regions,
  } = useRegionalTaxTypes({ accessToken, country });

  const region = useMemo(
    () => findFeeRegion({ country, regions, state }),
    [country, regions, state],
  );

  const regionTaxTypes = useMemo(
    () => getRegionTaxTypes({ feeTypes, regionId: region?._id }),
    [feeTypes, region],
  );

  const countryTaxTypes = useMemo(() => {
    const { federal, subregion } = getCountryTaxTypes({ feeTypes });

    return isSubregion ? subregion : federal;
  }, [feeTypes, isSubregion]);

  const { hasScopedTax: regionLeviesScopedTax, taxTypeKey } = resolveScopedTax({
    isSubregion,
    regionTaxTypes,
  });

  const isRegionRequired = isRegionSelectionRequired({
    areFeesLoading,
    hasCountryRegions: regions.some(
      (candidate) => candidate.country === country,
    ),
    isRegionResolved: Boolean(region),
  });

  const hasScopedTax = isScopedTaxAvailable({
    areFeesLoading,
    isRegionRequired,
    isSubregion,
    regionLeviesScopedTax,
  });

  const taxTypeName = resolveTaxTypeName({
    areFeesLoading,
    country,
    countryTaxTypes,
    intl,
    savedTaxTypes,
    taxTypeKey,
  });

  taxContext.current = {
    hasScopedTax,
    taxTypeName,
  };

  const selectedCountry = useMemo(
    () => findCountryByCode(allCountries, country),
    [allCountries, country],
  );

  // Every country, not just the ones this environment hosts in: a registration
  // held abroad is ordinary, and filtering would blank the country already on file.
  const countryOptions = useMemo(
    () => buildCountryOptions({ allCountries }),
    [allCountries],
  );
  const zoneOptions = useMemo(
    () => buildZoneOptions(selectedCountry),
    [selectedCountry],
  );

  const submit = async (values: TaxDetailsFormValues) => {
    // Or a resubmit shows the previous attempt's message.
    form.clearErrors("taxId");

    const result = await submitTaxDetailsForm({
      accessToken,
      dispatch,
      intl,
      isHost,
      isSubregion,
      registrationId,
      ssoId: ssoId ?? "",
      values,
    });

    if ("error" === result.status) {
      // On the tax ID, not the form: that is the field the guest has to fix.
      form.setError(
        "taxId",
        { message: result.message },
        { shouldFocus: true },
      );
    }
  };

  const value: TaxDetailsFormContextType = {
    countryOptions,
    form,
    hasScopedTax,
    isCountryLocked: getIsCountryLocked({ accountCountry }),
    isEdit,
    isHost,
    isRegionRequired,
    isSubregion,
    regionLabel: getRegionLabel({ intl, selectedCountry }),
    regionName: getRegionName({ intl, selectedCountry, state }),
    registration,
    selectedCountry,
    submit,
    taxTypeName,
    zoneOptions,
  };

  return (
    <TaxDetailsFormContext.Provider value={value}>
      {children}
    </TaxDetailsFormContext.Provider>
  );
};

const TaxDetailsFormProvider = injectIntl(TaxDetailsFormProviderComponent);

export { TaxDetailsFormProvider };
